import React, { useEffect, useState } from 'react';
import { Site } from '@99designs/site-navigation/lib/Site';
import { trackEvent } from '@99designs/tracking';
import { CategoryProductSchema } from '../../../shared/components/Schema';
import { colors } from '../../../shared/lib/styles';
import alternativeSites from '../../lib/alternativeSites';
import Grid from '../Grid';
import Head from '../Head';
import PageSection from '../PageSection';
import BundleFeatureComparisonSection from './BundleFeatureComparisonSection';
import { getBundles } from './BundleFeatureComparisonSection/bundles';
import { CategoryConfig } from './CategoryConfigProvider';
import DetailsSection from './DetailsSection';
import HeroSection from './HeroSection';
import HowItWorksSection from './HowItWorksSection';
import ResourcesSection from './ResourcesSection';
import ReviewsSection from './ReviewsSection';
import SupportSection from './SupportSection';

export interface Props {
    site: Site;
    randomFeaturedDesignerNumber: number;
    category: CategoryConfig;
}

function CategoryPageLayout({ site, randomFeaturedDesignerNumber, category }: Props) {
    const robotsMetaTagContent = 'index, follow, NOODP, NOYDIR';
    const [currentSection, setCurrentSection] = useState('');
    const bundles = category.bundleFeatureComparison
        ? getBundles(category.bundleFeatureComparison.bundleIds)
        : [];

    useEffect(() => {
        trackEvent('Viewed Product Page', { category: category.key });
    }, [category.key]);

    return (
        <>
            <Head
                title={category.pageTitle}
                description={category.description}
                meta={
                    <>
                        <meta name="robots" content={robotsMetaTagContent} />
                        {alternativeSites.map(([altLocale, domain]) => (
                            <link
                                rel="alternate"
                                hrefLang={altLocale}
                                key={altLocale}
                                href={`https://${domain}/${category.key}`}
                            />
                        ))}
                        <link
                            rel="canonical"
                            href={`https://${site.getDomain()}/${category.key}`}
                        />
                    </>
                }
                site={site}
            />
            <CategoryProductSchema
                categoryKey={category.key}
                productName={category.productSchema.name}
                description={category.productSchema.description}
                ratingValue={category.productSchema.ratingValue}
                reviewCount={category.productSchema.reviewCount}
            />

            <PageSection
                id="hero"
                pageName={category.key}
                onEnter={setCurrentSection}
                backgroundGradientColors={[colors.white, colors.grey100]}
            >
                <HeroSection
                    categoryKey={category.key}
                    categoryTitle={category.title}
                    hero={category.hero}
                    currentSection={currentSection}
                />
            </PageSection>

            {category.howItWorks && (
                <PageSection
                    id="how-it-works"
                    pageName={category.key}
                    onEnter={setCurrentSection}
                    paddingTop={['space10', 'space14']}
                    paddingBottom={['space7', 'space7']}
                >
                    <HowItWorksSection config={category.howItWorks} />
                </PageSection>
            )}

            {category.details && (
                <PageSection
                    id="details"
                    pageName={category.key}
                    onEnter={setCurrentSection}
                    paddingTop={['space10', 'space11']}
                    paddingBottom={['space10', 'space14']}
                >
                    <Grid>
                        <DetailsSection
                            categoryKey={category.key}
                            config={category.details}
                            randomFeaturedDesignerNumber={randomFeaturedDesignerNumber}
                        />
                    </Grid>
                </PageSection>
            )}

            {category.bundleFeatureComparison && (
                <PageSection
                    id="package-options"
                    pageName={category.key}
                    onEnter={setCurrentSection}
                    paddingTop={['space10', 'space11']}
                    paddingBottom={['space10', 'space14']}
                >
                    <Grid>
                        <BundleFeatureComparisonSection
                            title={category.bundleFeatureComparison.title}
                            description={category.bundleFeatureComparison.description}
                            featureComparison={category.bundleFeatureComparison.featureComparison}
                            bundles={bundles}
                        />
                    </Grid>
                </PageSection>
            )}

            <PageSection
                id="reviews"
                pageName={category.key}
                onEnter={setCurrentSection}
                backgroundGradientColors={[colors.grey100, colors.white]}
                paddingTop={['space10', 'space14']}
                paddingBottom={['none', 'space7']}
            >
                <ReviewsSection category={category} />
            </PageSection>

            <PageSection
                id="faq"
                pageName={category.key}
                onEnter={setCurrentSection}
                paddingTop={['space10', 'space14']}
                paddingBottom={['space10', 'space14']}
            >
                <Grid>
                    <SupportSection
                        categoryKey={category.key}
                        faqDescription={category.faq.description}
                        faqQuestions={category.faq.faqQuestions}
                        siteConfig={site.siteConfig}
                    />
                </Grid>
            </PageSection>

            <PageSection
                id="resources"
                pageName={category.key}
                onEnter={setCurrentSection}
                paddingTop={['space10', 'space14']}
                paddingBottom={['space10', 'space14']}
                backgroundGradientColors={[colors.grey100, colors.white]}
            >
                <Grid>
                    <ResourcesSection categoryKey={category.key} blogPosts={category.blogPosts} />
                </Grid>
            </PageSection>
        </>
    );
}

export default CategoryPageLayout;
